import { ApiError } from "@/pages/auth/api";
import { API_BASE_URL } from "@/shared/config/apiBaseUrl";

export type FederationRatings = {
  uscf?: number | null;
  cfc?: number | null;
  ecf?: number | null;
  dsb?: number | null;
  rcf?: number | null;
};

export type ActivitySummary = {
  games_played: number;
  moves_provided: number;
  play_games_by_day: Record<string, number>;
  livestream_guessed_right: number;
  livestream_guessed_wrong: number;
  livestream_timed_right: number;
  livestream_timed_wrong: number;
  livestream_paused_right: number;
  livestream_paused_wrong: number;
};

export type UserPublicProfile = {
  user_id: string;
  username: string;
  avatar_url?: string | null;
  title?: string | null;
  fide_rating?: number | null;
  china_title?: string | null;
  federation_ratings: FederationRatings;
  bio?: string | null;
  activity_summary: ActivitySummary;
};

export type UserPrivateProfile = {
  user_id: string;
  username: string;
  primary_email?: string | null;
  email_verified: boolean;
  avatar_url?: string | null;
  admin_or_not: boolean;
  donate_active: boolean;
  fide_rating?: number | null;
  title?: string | null;
  china_title?: string | null;
  federation_ratings: FederationRatings;
  bio?: string | null;
  activity_summary: ActivitySummary;
  created_at?: string | null;
  updated_at?: string | null;
};

export type UpdateProfilePayload = {
  fide_rating?: number | null;
  title?: string | null;
  china_title?: string | null;
  federation_ratings?: FederationRatings | null;
  bio?: string | null;
  avatar_url?: string | null;
};

export type UserActivityEvent = {
  event_id: string;
  event_type: string;
  occurred_at_utc: string;
  day_key_et: string;
  summary: string;
  payload: Record<string, unknown>;
};

export type UserActivityPage = {
  items: UserActivityEvent[];
  next_cursor?: string | null;
};

export type UserModelGame = {
  game_id: string;
  state: string;
  model_id: string;
  elo: number;
  hardness: number;
  temperature: number;
  user_color: string;
  model_color: string;
  white_name: string;
  black_name: string;
  time_control: {
    minutes: number;
    increment_seconds: number;
  };
  move_count: number;
  result: {
    winner: string | null;
    reason: string | null;
  } | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type UserModelGamePage = {
  items: UserModelGame[];
  next_cursor?: string | null;
};

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let detail = `request failed with status ${response.status}`;
    try {
      const payload = (await response.json()) as { detail?: string };
      if (payload.detail) {
        detail = payload.detail;
      }
    } catch {
      // Keep fallback message when response body is not JSON.
    }
    throw new ApiError(detail, response.status);
  }

  return (await response.json()) as T;
}

export async function getPublicProfile(username: string): Promise<UserPublicProfile> {
  const response = await fetch(`${API_BASE_URL}/api/user/${encodeURIComponent(username)}`);
  return parseJson<UserPublicProfile>(response);
}

export async function getOwnProfile(sessionToken: string): Promise<UserPrivateProfile> {
  const response = await fetch(`${API_BASE_URL}/api/user/me`, {
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
  });
  return parseJson<UserPrivateProfile>(response);
}

export async function getProfileActivity(
  username: string,
  before?: string | null,
): Promise<UserActivityPage> {
  const params = new URLSearchParams({ limit: "30" });
  if (before) {
    params.set("before", before);
  }
  const response = await fetch(
    `${API_BASE_URL}/api/user/${encodeURIComponent(username)}/activity?${params.toString()}`,
  );
  return parseJson<UserActivityPage>(response);
}

export async function getProfileModelGames(
  username: string,
  before?: string | null,
): Promise<UserModelGamePage> {
  const params = new URLSearchParams({ limit: "20" });
  if (before) {
    params.set("before", before);
  }
  const response = await fetch(
    `${API_BASE_URL}/api/user/${encodeURIComponent(username)}/model-games?${params.toString()}`,
  );
  return parseJson<UserModelGamePage>(response);
}

export async function updateOwnProfile(
  sessionToken: string,
  payload: UpdateProfilePayload,
): Promise<UserPrivateProfile> {
  const response = await fetch(`${API_BASE_URL}/api/user/me`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionToken}`,
    },
    body: JSON.stringify(payload),
  });

  return parseJson<UserPrivateProfile>(response);
}
